(ns manage.adset-decision-tree-page.view.table.base
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium useGridApiRef]]
            [cljs-bean.core :refer [->clj]]
            [clojure.set :as set]
            [common.flags :refer [use-flag]]
            [common.queries :refer [use-query]]
            [common.resource.query.adset-activation
             :refer [adset-activation-by-content-id-query
                     adset-activation-query
                     adset-tiktok-activation-query]]
            [common.util :as util]
            [common.widgets.data-grid :as dg]
            [manage.ad-variant.details-ocp.core :as-alias details-ocp]
            [manage.ad-variant.hooks :as ah]
            [manage.ad-variant.page.columns :as ad-variant-columns]
            [manage.ad-variant.page.table-util :as atu]
            [manage.ad-variant.queries :as q]
            [manage.adset-decision-tree-page.sub :as dt-sub]
            [manage.adset-decision-tree-page.view.context :as context]
            [manage.adset-decision-tree-page.view.header :refer [strategy-tab-actions]]
            [manage.adset-decision-tree-page.view.table-util :as tu]
            [manage.adset-decision-tree-page.view.table.meta :as meta-table]
            [manage.adset-decision-tree-page.view.table.tiktok :as tiktok-table]
            [manage.adset.hooks :refer [use-channel use-is-social-adset?]]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defhook use-columns []
  (let [has-grouping (ah/use-has-multiple-dimensions)]
    (uix/use-memo
     #(clj->js
       [(tu/get-activation-identifier-column has-grouping :decision-tree-node-immutable-id)
        (ad-variant-columns/get-ad-type-column {:show-scheduling true})
        ad-variant-columns/revision-id-column
        (cond-> tu/decision-path-column
          has-grouping (update :renderCell dg/render-only-group-rows))
        (assoc tu/activation-status-column :sortable false)
        tu/impressions-column
        tu/clicks-column
        tu/ctr-column
        tu/targeting-preview-column
        tu/navigate-to-node-column
        {:field "ad-variant-id"}
        ad-variant-columns/width-column
        ad-variant-columns/height-column])
     [has-grouping])))

(defn get-row-id [row] (:id row))

(defn union-aggregator
  [^js params]
  (apply set/union (.-values params)))

(defn sum-aggregator
  [^js params]
  (apply + (.-values params)))

(defn ctr-aggregator
  [^js params]
  (let [impressions (apply + (map first (.-values params)))
        clicks (apply + (map second (.-values params)))]
    [impressions clicks]))

(defn status-aggregator
  [^js params]
  (let [values (.-values params)]
    (cond
      (every? #{"draft"} values) "all draft"
      (some #{"draft"} values) "partially draft"
      (every? #{"removed"} values) "all removed"
      (every? #{"active"} values) "all active"
      :else "partially active")))

(def field->sort-field
  {(.-field ad-variant-columns/identifier-column) "content-id>"
   (:field tu/activation-status-column) "status>"})

(def field->resolver
  {(.-field ad-variant-columns/identifier-column) "content-id"
   (.-field ad-variant-columns/width-column) "ad-formats"
   (.-field ad-variant-columns/height-column) "ad-formats"
   (:field tu/activation-status-column) "status"})

(def field->column
  {(.-field ad-variant-columns/identifier-column) "content-id"
   (:field tu/activation-status-column) "status"})

(def query-filter-params
  {:field->sort-field field->sort-field
   :field->resolver field->resolver
   :field->column field->column
   :quick-filter {:field (.-field ad-variant-columns/identifier-column)}})

(defhook use-table-query-fn []
  (let [url-context (use-subscribe [:url-context])
        channel (use-channel)
        [query-fn filter-params] (case channel
                                   "tiktok" [adset-tiktok-activation-query
                                             tiktok-table/query-filter-params]
                                   "meta" [adset-activation-query
                                           meta-table/query-filter-params]
                                   [adset-activation-by-content-id-query
                                    query-filter-params])]
    (uix/use-callback (fn [table-models] (query-fn url-context filter-params table-models))
                      [url-context query-fn filter-params])))

(def table-sx
  (clj->js (merge dg/hover-action-sx
                  tu/hidden-aggregation-column-header-label-sx
                  tu/grey-out-row-without-status-sx
                  {".MuiDataGrid-columnHeader--pinnedLeft" {:borderRightStyle "none"}
                   ".MuiDataGrid-cell--pinnedLeft" {:borderRightStyle "none"}
                   ".MuiDataGrid-filler--pinnedLeft" {:borderRightStyle "none"}})))

(defn grey-out-removed-rows
  [{{:keys [getCellParams]} :current} {:keys [id]}]
  (when (#{"removed" "all removed"}
         (-> (getCellParams id "activationStatus") ->clj :value))
    "greyed-out"))

(defui ad-variant-pre-filter [{:keys [filter-model set-filter-model]}]
  (let [variant-uuid (some #(when (= (:field %) "ad-variant-id")
                              (first (:value %)))
                           (:items (->clj filter-model)))
        {:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {{:keys [content-id]} :data} (use-query (q/ad-variant-by-uuid-query
                                                 {:advertiser-id advertiser-id
                                                  :adset-id adset-id
                                                  :ad-uuid variant-uuid}))]
    (when variant-uuid
      ($ Chip {:size "medium"
               :label content-id
               :sx #js {:mr "auto"}
               :onDelete (fn []
                           (set-filter-model
                            (clj->js (update (->clj filter-model)
                                             :items (fn [items]
                                                      (remove #(= (:field %) "ad-variant-id")
                                                              items)))))
                           (rfe/set-query (fn [query-params]
                                            (dissoc query-params :ad-variant-filter))))}))))

(def Toolbar
  (uix/as-react
   (fn [{:keys [additional-filters custom-filter-components status-filter]}]
     ($ dg/grid-toolbar
        ($ Box {:sx #js {:flex 1}}
           ($ dg/filters-button
              {:variant :ocp
               :filters (into [(or status-filter tu/status-filter)] additional-filters)
               :custom-filter-components (concat custom-filter-components
                                                 [{:id "ad-variant-pre-filter"
                                                   :component ad-variant-pre-filter}])}))
        ($ strategy-tab-actions)))))

(defui table
  [{:keys [columns additional-filters pinned-columns aggregation get-row-id initial-filter-model get-ad-variant-id disable-grouping custom-filter-components]}]
  (let [api-ref (useGridApiRef)
        table-query-fn (use-table-query-fn)
        status-filter (if (use-is-social-adset?)
                        tu/combined-status-filter
                        tu/status-filter)

        row-grouping-props (ah/use-ad-variant-grouping-props
                            {:api-ref api-ref
                             :disable-grouping disable-grouping
                             :groups-opened-by-default false
                             :initial-column-visibility-model
                             {:format false
                              :template false
                              :ad-variant-id false
                              :freetext false
                              :width false
                              :height false
                              (:field tu/integration-error-filter) false}})
        server-table-props (dg/use-server-table-props
                            {:initial-pagination-model #js {:page 0 :pageSize 10}
                             :table-query-fn table-query-fn
                             :prevent-loading-on-refetch true
                             :row-grouping-model (:rowGroupingModel row-grouping-props)
                             :initial-filter-model initial-filter-model})
        get-row-classname (uix/use-callback
                           (fn [^js params]
                             (grey-out-removed-rows (->clj api-ref) (->clj params)))
                           [api-ref])
        immutable-node-id->node-data (use-subscribe [::dt-sub/immutable-node-id->node-data])
        activation-details-side-panel-enabled (use-flag :activation-details-side-panel)
        on-row-click (uix/use-callback
                      (fn [^js params]
                        (let [{:keys [decision-tree-node-immutable-id] :as strategy-row}
                              (->clj (.-row params))]
                          (when activation-details-side-panel-enabled
                            (when-let [ad-variant-id (get-ad-variant-id strategy-row)]
                              (rf/dispatch
                               [:ocp/open
                                {:id ::details-ocp/ocp
                                 :props {:variant-uuid ad-variant-id
                                         :strategy-row strategy-row
                                         :assigned-node-data
                                         (get immutable-node-id->node-data
                                              decision-tree-node-immutable-id)}}])))))
                      [immutable-node-id->node-data get-ad-variant-id activation-details-side-panel-enabled])
        {:keys [activation-processing]} (uix/use-context context/decision-trees-context)]
    ($ DataGridPremium
       (merge
        (update server-table-props :loading #(or % activation-processing))
        row-grouping-props
        {:apiRef api-ref
         :columns columns
         :columnHeaderHeight 50
         :getRowId get-row-id
         :getRowClassName get-row-classname
         :disableAggregation false
         :disableColumnMenu true
         :onRowClick on-row-click
         :pageSizeOptions #js [10 20 50]
         :slots #js {:toolbar Toolbar}
         :slotProps #js {:baseTextField dg/grid-toolbar-outlined-input
                         :toolbar #js {:additional-filters additional-filters
                                       :custom-filter-components custom-filter-components
                                       :status-filter status-filter}}
         :disableRowSelectionOnClick true
         :aggregationFunctions #js {:first #js {:apply dg/first-aggregator}
                                    :union #js {:apply union-aggregator}
                                    :status #js {:apply status-aggregator}
                                    :sum #js {:apply sum-aggregator}
                                    :ctr #js {:apply ctr-aggregator}}
         :getAggregationPosition dg/hide-root-group-row
         :pinnedColumns pinned-columns
         :initialState (util/merge-objects
                        #js {:aggregation #js {:model aggregation}}
                        (:initialState row-grouping-props))
         :noWrap true
         :sx table-sx}))))

(defhook use-activation-ad-variant-dg-filter-item [ad-variant-uuid]
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {{revisions :data} :data revisions-loading :isLoading}
        (use-query (q/ad-variant-revisions-query {:advertiser-id advertiser-id
                                                  :adset-id adset-id
                                                  :ad-variant-uuid ad-variant-uuid}))]
    (uix/use-memo (fn []
                    (when-not revisions-loading
                      #js {:id "ad-variant-id"
                           :field "ad-variant-id"
                           :operator "isAnyOf"
                           :value (to-array (map (comp :uuid :data) revisions))}))
                  [revisions-loading revisions])))

(def hide-removed-rows-filter-item {:id "status"
                                    :field (:field tu/activation-status-column)
                                    :operator "isAnyOf"
                                    :value ["pending" "active" "paused"]})

(def combined-hide-removed-rows-filter-item
  "Meta/TikTok variant of `hide-removed-rows-filter-item`: every non-removed value
   of the combined `activation-status` column."
  {:id "status"
   :field (:field tu/activation-status-column)
   :operator "isAnyOf"
   :value ["pending"
           "active-serving"
           "active-not-serving"
           "inactive-scheduled"
           "inactive-manual"]})

(defui initial-filtered-table [{:keys [initial-filter-model] :as props}]
  (let [{:keys [ad-variant-filter show-removed]} (use-subscribe [:router/query-parameters])
        hide-removed-item (if (use-is-social-adset?)
                            combined-hide-removed-rows-filter-item
                            hide-removed-rows-filter-item)
        dg-filter-item (use-activation-ad-variant-dg-filter-item ad-variant-filter)
        initial-filter-model
        (uix/use-memo
         (fn []
           (cond-> initial-filter-model
             (not show-removed) (update :items conj hide-removed-item)
             (and ad-variant-filter dg-filter-item) (update :items conj dg-filter-item)

             true clj->js))
         [ad-variant-filter dg-filter-item initial-filter-model show-removed hide-removed-item])]
    (when (or (not ad-variant-filter) dg-filter-item)
      ($ table (assoc props :initial-filter-model initial-filter-model)))))

(defui base-table []
  ($ initial-filtered-table {:columns (use-columns)
                             :custom-filter-components [{:component atu/template-radio-filter
                                                         :id "template-radio-filter"
                                                         :format-field "format"}]
                             :aggregation #js {:decisionPath "first"
                                               :navigateToNode "first"
                                               :preview "union"
                                               :activationStatus "status"
                                               :impressions "sum"
                                               :clicks "sum"
                                               :ctr "ctr"}
                             :get-row-id get-row-id
                             :get-ad-variant-id :ad-variant-id}))

(defui tiktok-table []
  ($ initial-filtered-table {:columns (tiktok-table/use-columns)
                             :custom-filter-components [{:component atu/template-radio-filter
                                                         :id "template-radio-filter"
                                                         :format-field "format"}]
                             :initial-filter-model {:items []}
                             :pinned-columns #js {:left #js ["enabled"]}
                             :aggregation tiktok-table/aggregation
                             :get-row-id tiktok-table/get-row-id
                             :get-ad-variant-id tiktok-table/get-ad-variant-id}))

(defui meta-table []
  ($ initial-filtered-table {:columns meta-table/columns
                             :additional-filters meta-table/additional-filters
                             :pinned-columns #js {:left #js ["enabled"]}
                             :disable-grouping true
                             :get-row-id meta-table/get-row-id
                             :get-ad-variant-id meta-table/get-ad-variant-id}))
